<template>
  <button
    :id="id"
    :class="buttonComponentClasses"
    :disabled="disabled"
    :type="type"
    @click="$emit('click', $event)"
    @mouseover="$emit('mouseover', $event)"
  >
    <slot name="prependSlot" />
    <slot name="contentSlot">
      <loader v-if="loading" />
      <template v-else>
        <span
          v-if="prependIconClass"
          :class="getPrependIconClass"
          @mouseover="$emit('prepend-icon-mouseover', $event)"
        />
        <span v-if="buttonText" @mouseover="$emit('text-mouseover', $event)" v-text="buttonText" />
        <span v-if="appendIconClass" :class="getAppendIconClass" @mouseover="$emit('append-icon-mouseover', $event)" />
      </template>
    </slot>
    <slot name="appendSlot" />
  </button>
</template>

<script>
export default {
  name: 'KwButton',
  props: {
    appendIconClass: {
      type: String,
      required: false,
      default: '',
    },
    buttonActive: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    buttonText: {
      type: [String, Number],
      required: false,
      default: '',
    },
    // primary, secondary, text
    buttonType: {
      type: String,
      required: false,
      default: 'primary',
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    prependIconClass: {
      type: String,
      required: false,
      default: '',
    },
    borderedPrependIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    borderedAppendIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      type: String,
      required: false,
      default: 'button',
    },
    id: {
      required: false,
      type: String || Number,
      default: `kw-button-${new Date().getMilliseconds()}`,
    },
    buttonClasses: {
      type: String,
      required: false,
    },
  },
  computed: {
    disabled() {
      return this.buttonDisabled || this.loading;
    },
    buttonComponentClasses() {
      return {
        'kw-button': true,
        [`kw-btn-${this.buttonType}`]: true,
        'btn-active': this.buttonActive,
        ...(this.buttonClasses ? Object.fromEntries(this.buttonClasses.split(' ').map((cls) => [cls, true])) : {}),
      };
    },
    getPrependIconClass() {
      return {
        'right-margin': this.prependIconClass && this.buttonText,
        [this.prependIconClass]: true,
        'right-borderd': this.borderedPrependIcon,
      };
    },
    getAppendIconClass() {
      return {
        'left-margin': this.appendIconClass && this.buttonText,
        [this.appendIconClass]: true,
        'left-bordered': this.borderedAppendIcon,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
button {
  &.kw-button {
    font-size: 14px;
    font-weight: 500;
    padding: 8px 16px;
    height: 36px;

    &.kw-btn-default {
      border-radius: 4px;
      background-color: kw-color(kw-grey, 1);
      color: kw-color(kw-white, 1);
      transition: background-color 0.3s;
      justify-content: center;
      @include box-shadow-2;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-grey, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-grey, 6);
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-grey, 1);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-grey, 7);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-grey, 1);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-primary {
      border-radius: 4px;
      background-color: kw-color(kw-blue, 1);
      color: kw-color(kw-white, 1);
      transition: background-color 0.3s;
      justify-content: center;
      @include box-shadow-2;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-blue, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-blue, 6);
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-blue, 1);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-blue, 7);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-blue, 1);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-action {
      border-radius: 4px;
      background-color: kw-color(kw-red, 10);
      color: kw-color(kw-white, 1);
      transition: background-color 0.3s;
      justify-content: center;
      @include box-shadow-2;

      ::v-deep.button-loader {
        opacity: 0.7;
        border-color: transparent transparent transparent kw-color(kw-white, 1) !important;
      }

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-red, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-red, 10);
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-red, 10);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-red, 10);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-red, 10);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-outline-action {
      border-radius: 4px;
      background-color: kw-color(kw-white, 1);
      border: 1px solid kw-color(kw-red, 10);
      color: kw-color(kw-red, 10);
      transition: background-color 0.3s;
      justify-content: center;
      @include box-shadow-2;

      ::v-deep.button-loader {
        opacity: 0.7;
        border-color: transparent transparent transparent kw-color(kw-red, 10) !important;
      }

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-white, 1);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-white, 1);
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-white, 1);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-white, 1);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-white, 1);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-primary-disabled {
      border-radius: 4px;
      background-color: kw-color(kw-blue, 1);
      color: kw-color(kw-white, 1);
      transition: background-color 0.3s;
      justify-content: center;
      opacity: 0.5;
      @include box-shadow-2;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-blue, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-blue, 1);
        opacity: 1;
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-blue, 1);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-blue, 7);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-blue, 1);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-outline-primary {
      padding: 0 8px;
      border-radius: 4px;
      color: kw-color(kw-blue, 1);
      border: 1px solid kw-color(kw-blue, 1);
      background-color: kw-color(kw-white, 1);
      transition: color 0.3s;
      justify-content: center;

      ::v-deep.button-loader {
        opacity: 0.7;
        border-color: transparent transparent transparent kw-color(kw-blue, 1) !important;
      }

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-blue, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        color: kw-color(kw-blue, 6);

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                color: kw-color(kw-blue, 1);
              }
            }
          }
        }
      }

      &:focus {
        color: kw-color(kw-blue, 7);
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          color: kw-color(kw-blue, 1);
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-secondary {
      border-radius: 4px;
      border: 1px solid kw-color(kw-light-grey, 3);
      background-color: kw-color(kw-white, 1);
      color: kw-color(kw-blue, 1);
      justify-content: center;
      @include box-shadow-1;
      transition: 0.2s;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-light-grey, 3);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        border: 1px solid kw-color(kw-blue, 6);

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-blue, 6);
              }
            }
          }
        }
      }

      &:focus {
        border: 1px solid kw-color(kw-blue, 7);
        color: kw-color(kw-blue, 7);

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-blue, 7);
              }
            }
          }
        }
      }

      &:disabled {
        opacity: 0.5;
        border: 1px solid kw-color(kw-blue, 1);
        color: kw-color(kw-blue, 1);

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-blue, 1);
              }
            }
          }
        }

        &:hover {
          opacity: 0.5;
          border: 1px solid kw-color(kw-blue, 1);
        }
      }
    }

    &.kw-btn-danger {
      border-radius: 4px;
      background-color: kw-color(kw-red, 1);
      color: kw-color(kw-white, 1) !important;
      transition: background-color 0.3s;
      justify-content: center;
      @include box-shadow-2;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              background-color: kw-color(kw-red, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        background-color: kw-color(kw-red, 6);
        @include box-shadow-3;

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                background-color: kw-color(kw-red, 1);
              }
            }
          }
        }
      }

      &:focus {
        background-color: kw-color(kw-red, 7);
        @include box-shadow-1;
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-red, 1);
          @include box-shadow-2;
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-outline-danger {
      padding: 0 8px;
      border-radius: 4px;
      color: kw-color(kw-red, 1);
      background-color: kw-color(kw-white, 1);
      transition: color 0.3s;
      justify-content: center;

      ::v-deep.button-loader {
        opacity: 0.7;
        border-color: transparent transparent transparent kw-color(kw-red, 1) !important;
      }

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;

          &.left-bordered {
            position: relative;
            margin-left: 30px;

            &:after {
              content: '';
              height: 40px;
              width: 1px;
              color: kw-color(kw-red, 6);
              position: absolute;
              top: -13px;
              left: -15px;
            }
          }
        }
      }

      &:hover {
        color: kw-color(kw-red, 6);

        span {
          &.left-margin {
            &.left-bordered {
              &:after {
                color: kw-color(kw-red, 1);
              }
            }
          }
        }
      }

      &:focus {
        color: kw-color(kw-red, 7);
      }

      &:disabled {
        opacity: 0.5;

        &:hover {
          color: kw-color(kw-red, 1);
          opacity: 0.5;
        }
      }
    }

    &.kw-btn-text {
      color: #596068;
      padding: 0 0;
      justify-content: center;
      transition: 0.3s;

      span {
        &.right-margin {
          margin-right: 9px;
          font-weight: 700;
        }

        &.left-margin {
          margin-left: 9px;
          font-weight: 700;
          font-size: 18px;
        }
      }

      &:hover {
        color: kw-color(kw-blue, 1);
      }

      &:disabled {
        opacity: 0.5;
        color: #596068;
      }
    }
    &.rankscale {
      background-color: kw-color(kw-purple, 11);
      &:hover,
      &:active,
      &:focus {
        background-color: kw-color(kw-purple, 10);
      }
      &:disabled {
        opacity: 0.5;

        &:hover {
          background-color: kw-color(kw-purple, 10);
          opacity: 0.5;
        }
      }
    }
    // ::v-deep.button-loader {
    //   background-color: kw-color(kw-white, 1);
    //   position: absolute;
    //   left: 7px;
    // }
  }
}
</style>

<!--how to use
  <kw-button
    :button-text="$t('share')"
    button-type="primary"
    append-icon-class="kw kw-airplay"
    prepend-icon-class="kw kw-airplay"
    bordered-append-icon
    bordered-prepend-icon
  />
-->
